import { View } from "@-/com.npmjs.react-native";
import Animated, {
  cancelAnimation,
  Easing,
  type SharedValue,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { HanStreakFlame } from "@-/dev.improve.haniverse.assets.icons";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  type DailyLoginRewardDay,
  DAILY_LOGIN_REWARD_CYCLE,
  describeHanReward,
  HAN_COIN_ICON_CLASS_NAME,
  type HanReward,
} from "@-/dev.improve.haniverse.lib.han-currency";
import { useEffect, useState } from "react";
import type { LearnSummaryDailyRewardGate } from "#use-learn-summary-daily-reward";

type DayRewardMark = "HanCashCoin" | "MdFlightTakeoff";

/**
 * A day's reward as its column shows it: a mark (the coin, a plane for HanMiles, or none for a
 * dragon, whose 龙 is the amount) and the whole amount, never abbreviated (`2500`, not `2.5k`).
 */
function dayRewardDisplay(reward: HanReward): {
  amount: string;
  caption: string | null;
  mark: DayRewardMark | null;
} {
  if (reward.dragons > 0) {
    return {
      amount: reward.dragons === 1 ? "龙" : `${reward.dragons} 龙`,
      caption: null,
      mark: null,
    };
  }
  if (reward.hanMiles > 0) {
    return { amount: String(reward.hanMiles), caption: null, mark: "MdFlightTakeoff" };
  }
  if (reward.reviewBonusHan > 0) {
    return { amount: `+${reward.reviewBonusHan}`, caption: "/review", mark: "HanCashCoin" };
  }
  return { amount: String(reward.han), caption: null, mark: "HanCashCoin" };
}

/** Four digits (`1000`, `2500`) take a smaller size, so a whole amount fits a seventh of the row. */
function isLongRewardAmount(amount: string): boolean {
  return amount.length > 3;
}

function dayRewardMarkClassName(mark: DayRewardMark, inkClassName: string): string {
  return mark === "HanCashCoin" ? HAN_COIN_ICON_CLASS_NAME : inkClassName;
}

const REWARD_SHIFT_MS = 480;
const MISSED_REVEAL_MS = 180;
/** The gap between day tiles (`gap-1`), as the due-review tiles below space theirs. */
const DAY_TILE_GAP = 4;

function RewardContent({ isCollected, reward }: { isCollected: boolean; reward: HanReward }) {
  const { amount, caption, mark } = dayRewardDisplay(reward);
  const isLong = isLongRewardAmount(amount);
  const markSize = isLong ? 11 : 13;
  return (
    <>
      <View className="flex-row items-center gap-0.5">
        {isCollected ? (
          <Icon className="text-yellow-700 dark:text-yellow-300" name="MdCheck" size={markSize} />
        ) : mark === null ? null : (
          <Icon
            className={dayRewardMarkClassName(mark, "text-foreground")}
            name={mark}
            size={markSize}
          />
        )}
        <Text
          className={
            isLong ? "font-sans text-[11px] font-semibold" : "font-sans text-[13px] font-semibold"
          }
          colorVariant={isCollected ? "muted" : "default"}
          numberOfLines={1}
        >
          {amount}
        </Text>
      </View>
      {caption === null ? null : (
        <Text
          className="font-sans text-[10px] leading-[12px]"
          colorVariant="muted"
          numberOfLines={1}
        >
          {caption}
        </Text>
      )}
    </>
  );
}

function MovingReward({
  isCollected = false,
  progress,
  reward,
  shift,
  stride,
}: {
  isCollected?: boolean;
  progress: SharedValue<number>;
  reward: HanReward;
  shift: number;
  stride: number;
}) {
  const style = useAnimatedStyle(() => ({
    transform: [{ translateX: -(1 - progress.get()) * shift * stride }],
  }));
  return (
    <Animated.View className="items-center gap-0.5" style={style}>
      <RewardContent isCollected={isCollected} reward={reward} />
    </Animated.View>
  );
}

function MissedDay({ reveal }: { reveal: SharedValue<number> }) {
  const style = useAnimatedStyle(() => ({ opacity: reveal.get() }));
  return (
    <Animated.View className="items-center" style={style} testID="daily-reward-missed-day">
      <HanStreakFlame accessible={false} size={18} variant="inactive" />
    </Animated.View>
  );
}

/**
 * One day of the week as its own square tile, like a character tile: its reward above its weekday,
 * centred. Today's unopened reward is orange and an opened day yellow; any other day keeps the
 * tiles' white surface.
 */
function DayTile({
  day,
  isClaimable,
  isCollected,
  progress,
  reveal,
  shift,
  stride,
}: {
  day: DailyLoginRewardDay;
  isClaimable: boolean;
  isCollected: boolean;
  progress: SharedValue<number>;
  reveal: SharedValue<number>;
  shift: number;
  stride: number;
}) {
  return (
    <View
      className={
        isClaimable
          ? "aspect-square flex-1 items-center justify-between gap-0.5 rounded-xs border border-orange-300 bg-orange-100 py-1.5 dark:border-orange-400/40 dark:bg-orange-400/20"
          : isCollected
            ? "aspect-square flex-1 items-center justify-between gap-0.5 rounded-xs border border-yellow-300 bg-yellow-100 py-1.5 dark:border-yellow-400/40 dark:bg-yellow-400/20"
            : "aspect-square flex-1 items-center justify-between gap-0.5 rounded-xs border border-border bg-surface py-1.5"
      }
      collapsable={false}
    >
      {/* The weekday keeps to the bottom edge and the reward is centred above it, so a two-line
          reward (a review bonus) keeps every weekday level. */}
      <View className="min-h-5 grow items-center justify-center">
        {day.reward === null ? (
          day.status === "missed" ? (
            <MissedDay reveal={reveal} />
          ) : (
            <Text colorVariant="muted" testID="daily-reward-unavailable-day">
              -
            </Text>
          )
        ) : (
          <MovingReward
            isCollected={isCollected}
            progress={progress}
            reward={day.reward}
            shift={day.status === "collected" ? 0 : shift}
            stride={stride}
          />
        )}
      </View>
      <Text
        className={
          isClaimable
            ? "font-sans text-[11px] leading-[14px] font-semibold text-orange-700 dark:text-orange-300"
            : "font-sans text-[11px] leading-[14px]"
        }
        colorVariant="muted"
        numberOfLines={1}
      >
        {day.label}
      </Text>
    </View>
  );
}

/**
 * Today's unopened 红包 on Continue Learning, beside its label (`+10`, `+2500`): pressing the
 * button opens it. Drawn on the button's inverted fill, with the coin in its own gold.
 */
export function LearnSummaryDailyRewardBadge({ reward }: { reward: HanReward }) {
  const { amount, caption, mark } = dayRewardDisplay(reward);
  return (
    <View
      className="flex-row items-center gap-0.5 rounded-full bg-background/15 px-2 py-0.5"
      testID="learn-map-welcome-start-reward"
    >
      {mark === null ? null : (
        <Icon className={dayRewardMarkClassName(mark, "text-background")} name={mark} size={14} />
      )}
      <Text className="text-sm font-semibold text-background" numberOfLines={1}>
        {/* A review bonus is already signed (`+1/review`). */}
        {caption === null ? `+${amount}` : `${amount}${caption}`}
      </Text>
    </View>
  );
}

/**
 * The calendar week's 红包 across the top of the Welcome Back card: a row of seven square day
 * tiles, opened days yellow and today's unopened reward orange, each with its reward above its
 * weekday. The tiles are informational; Continue Learning opens today's reward. Collected days
 * stay ticked off and the row stays put so the grid below never jumps.
 */
export function LearnSummaryDailyReward({ gate }: { gate: LearnSummaryDailyRewardGate }) {
  const isOffered = gate.kind === "offer";
  const week = gate.week;
  const todayIndex = week.findIndex((day) => day.status === "today");
  let lastCollectedIndex = -1;

  for (const [index, day] of week.entries()) {
    if (!(index in week)) {
      continue;
    }

    lastCollectedIndex = day.status === "collected" ? index : lastCollectedIndex;
  }
  const shift = Math.max(0, todayIndex - lastCollectedIndex - 1);
  const reduceMotion = useReducedMotion();
  const progress = useSharedValue(reduceMotion || shift === 0 ? 1 : 0);
  const reveal = useSharedValue(reduceMotion || shift === 0 ? 1 : 0);
  const alreadyRevealed = useSharedValue(1);
  const [rowWidth, setRowWidth] = useState(0);
  const tileSize = Math.max(0, (rowWidth - DAY_TILE_GAP * 6) / 7);
  const stride = tileSize + DAY_TILE_GAP;
  useEffect(() => {
    if (rowWidth === 0) {
      return;
    }
    if (reduceMotion || shift === 0) {
      progress.set(1);
      reveal.set(1);
      return;
    }
    progress.set(0);
    reveal.set(0);
    progress.set(withTiming(1, { duration: REWARD_SHIFT_MS, easing: Easing.inOut(Easing.cubic) }));
    reveal.set(withDelay(REWARD_SHIFT_MS, withTiming(1, { duration: MISSED_REVEAL_MS })));
    return () => {
      cancelAnimation(progress);
      cancelAnimation(reveal);
    };
  }, [progress, reduceMotion, reveal, rowWidth, shift]);
  // These start inside the week and leave through its right edge during the shift.
  const overflowRewards = DAILY_LOGIN_REWARD_CYCLE.slice(gate.cycleDay - 1 + (7 - todayIndex));
  const upcoming = week
    .filter((day) => day.status === "upcoming")
    .map(
      (day) => `${day.label}, ${day.reward === null ? "no reward" : describeHanReward(day.reward)}`,
    )
    .join("; ");
  const missed = week
    .filter((day) => day.status === "missed")
    .map((day) => `${day.label}: no reward`)
    .join("; ");
  return (
    <View
      accessibilityHint={isOffered ? "Press Continue Learning to open it." : undefined}
      accessibilityLabel={
        isOffered
          ? `Today's red envelope, day ${gate.cycleDay} of 7: ${describeHanReward(gate.reward)}`
          : `Today's red envelope collected: ${describeHanReward(gate.reward)}`
      }
      accessibilityValue={{
        text: [missed, upcoming ? `Coming up: ${upcoming}` : "Final day of this week"]
          .filter(Boolean)
          .join("; "),
      }}
      accessible
      // No frame of its own: the tiles sit side by side. It clips the rewards that slide out past
      // the last tile, and caps the tiles' size on a wide window.
      className="w-full max-w-xl self-center overflow-hidden"
      testID={isOffered ? "learn-summary-daily-reward" : "learn-summary-daily-reward-collected"}
    >
      <View
        className="flex-row gap-1"
        onLayout={(event) => setRowWidth(event.nativeEvent.layout.width)}
      >
        {week.map((day, index) => (
          <DayTile
            day={day}
            isClaimable={day.status === "today" && isOffered}
            isCollected={day.status === "collected" || (day.status === "today" && !isOffered)}
            key={day.dayKey}
            progress={progress}
            reveal={index > lastCollectedIndex ? reveal : alreadyRevealed}
            shift={shift}
            stride={stride}
          />
        ))}
        {rowWidth === 0
          ? null
          : overflowRewards.map((reward, index) => (
              // Laid out like a day tile (reward, then room for a weekday), so it rides level with
              // the rewards in the tiles.
              <View
                accessibilityElementsHidden
                className="absolute top-0 items-center justify-between gap-0.5 border border-transparent py-1.5"
                importantForAccessibility="no-hide-descendants"
                key={`overflow-${gate.cycleDay + index}`}
                pointerEvents="none"
                style={{ height: tileSize, left: (7 + index) * stride, width: tileSize }}
              >
                <View className="min-h-5 grow items-center justify-center">
                  <MovingReward progress={progress} reward={reward} shift={shift} stride={stride} />
                </View>
                <View className="h-[14px]" />
              </View>
            ))}
      </View>
    </View>
  );
}
